import { ApiClientProvider } from "@gladis/api-client";
import { loadCatalogue } from "@gladis/ui/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import "./app.css";

import { apiClient, authBridge, restoreSession } from "./lib/api.js";
import { AuthProvider, useAuth } from "./lib/auth-context.js";
import { router } from "./router.js";

/**
 * Amounts are written the way the READER writes numbers — €1.000,00 in Dutch, €1,000.00 in English —
 * and the currency is EUR either way, because it is the only one there is (Principle VII).
 *
 * ==========================================================================================
 * AMENDED BY FEATURE 021. THIS USED TO SAY "€1,000.00 in Boston", AND THAT IS NO LONGER TRUE.
 * ==========================================================================================
 *
 * `b7a058f` made the formatter locale-aware and called it here with
 * `navigator.languages?.[0] ?? navigator.language`, on the argument that "a member in Boston sees
 * the household's euros written the way they read numbers". That was the right argument for the
 * choice it was making — between a hardcoded `nl-NL` and something that adapts at all — and it does
 * not survive the control 021 added.
 *
 * The reason is FR-041: each LANGUAGE carries exactly one formatting convention, `nl-NL` and
 * `en-GB`, and the region the browser reports decides nothing. Two things forced it. An explicit
 * language choice that wins permanently is precisely what lets the words and the figures disagree,
 * so the figures now follow the resolved language rather than the browser. And the E2E suite is
 * pinned to one language across 771 selectors that match on visible text; a date that followed the
 * runner's region would be a non-deterministic assertion.
 *
 * (Phrased carefully: `colour-tokens-exist.test.ts` reads whole files, comments included, and takes
 * any hyphenated word beginning with a colour prefix for a Tailwind utility. It refused this file
 * twice while this comment was being written — once for the phrase, and once for the parenthetical
 * quoting the phrase. Its note on `box` explains why it does not strip comments, and the convention
 * it settled on is that prose avoids looking like a class.)
 *
 * So a member in Boston reading English sees `€1,234.50` and `17 August 2026`. The euros are still
 * written the way English writes numbers. What they have lost is American date order, and that is a
 * real narrowing, recorded here rather than discovered later.
 *
 * The call itself has moved into `loadCatalogue` at the foot of this file, so the words, the
 * document's `lang` and the shape of every figure are settled by one call and cannot drift apart.
 * The reasoning about WHY it happens before the first paint is unchanged and now lives there.
 */

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

/**
 * Connects the API client to React state.
 *
 * The client is created once at module scope and cannot use hooks, but it needs to hand a renewed
 * token back into state and to route to sign-in when the session is unrecoverable. This wires those
 * two callbacks once, on mount.
 */
function AuthBridge({ children }: { children: ReactNode }): ReactNode {
  const { setAccessToken, clear } = useAuth();

  useEffect(() => {
    authBridge.setAccessToken = setAccessToken;
    authBridge.onUnauthenticated = () => {
      clear();
      queryClient.clear();
    };
  }, [setAccessToken, clear]);

  return <>{children}</>;
}

/**
 * On a cold load the access token is gone — it lives in memory only, by design. Try once to renew
 * it from the refresh cookie before rendering, so a signed-in person who reloads is not bounced to
 * sign-in.
 */
function Bootstrap({ children }: { children: ReactNode }): ReactNode {
  const { setAccessToken, finishRestoring } = useAuth();

  useEffect(() => {
    // `restoreSession` already writes the token through the bridge; `setAccessToken` is listed as a
    // dependency because the bridge is wired from it.
    void restoreSession().finally(finishRestoring);
  }, [setAccessToken, finishRestoring]);

  return <>{children}</>;
}

/**
 * THE CATALOGUE IS AWAITED BEFORE THE FIRST RENDER, AND THAT RECONCILES TWO RULES THAT LOOK OPPOSED.
 *
 * FR-043 says the catalogue must not reach the first-paint path; FR-045 says the first screen must
 * render real strings. Both hold here, because they are about different things:
 *
 *   The `import()` is DYNAMIC, so neither language's copy is in the chunk that draws the shell, and
 *   the language nobody is reading is never fetched at all. That is the `@gladis/ui/motion` lesson —
 *   a barrel import whose only symptom was a one-in-four flake in a spec three features away.
 *
 *   It is AWAITED HERE rather than resolved in an effect or behind Suspense, so nothing renders
 *   before the words exist. This is the same argument the money locale makes above, with far more
 *   surface: an effect runs after the first paint, so the first screen would render in one language
 *   and change to another under the reader.
 *
 * `loadCatalogue` also stamps `<html lang>` and sets the money locale, so the words, the document's
 * declared language and the shape of every figure are settled by one call and cannot disagree.
 *
 * The inline script in `index.html` has already stamped `lang` for assistive technology; this
 * re-stamps the same value from the module that owns it. They agree by construction, and
 * `theme-contract.test.ts` asserts the literals they duplicate still match.
 */
await loadCatalogue();

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthBridge>
          <Bootstrap>
            <ApiClientProvider client={apiClient}>
              <RouterProvider router={router} />
            </ApiClientProvider>
          </Bootstrap>
        </AuthBridge>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
